import { useState } from "react";
import { Card } from "../../components/common/Card";
import { EmptyState } from "../../components/common/EmptyState";
import { describeActionDetails } from "../../services/hiveActions";
import { WeatherPanel } from "../../components/sites/WeatherPanel";

const ACTION_ICONS = {
  Inspection: "🔍", HealthCheck: "🩺",
  CloseSeason: "🍂", IssueFound: "⚠️", PulledFrames: "🖼️", ReplacedFrames: "🔄",
  AddedFrames: "➕", RotatedFrames: "🔁",
  Split: "🌱", Merged: "🤝", SwarmCaptured: "🐝",
  EquipmentAdded: "🧰", EquipmentRemoved: "🧰",
  // The end of the colony (#432). Deliberately sombre rather than
  // alarming: it is a record of something that already happened, not
  // a warning about something that might.
  ColonyLost: "🪦",
};

// Equipment moves are frequent and low-drama, and the timeline is what
// people read to understand THE BEES (V075). Twelve of them interleaved
// with two inspections makes the inspections hard to find, so they can be
// switched off -- which is only possible because they carry their own
// action types rather than borrowing AddedFrames.
const EQUIPMENT_ACTIONS = new Set(["EquipmentAdded", "EquipmentRemoved"]);

// Readable labels. "EquipmentAdded" is a database value, not a sentence.
const ACTION_LABELS = {
  EquipmentAdded: "Equipment added",
  EquipmentRemoved: "Equipment removed",
  SwarmCaptured: "Swarm captured",
  // "ColonyLost" is a database value, not a sentence -- and this is
  // the row somebody will be reading in March wondering what
  // happened in November.
  ColonyLost: "Colony lost",
};

/* How much later the system heard about it than it happened (#269).
   Only shown when the two genuinely differ by more than a few minutes:
   anything logged online has them a second apart, and "synced 0 minutes
   later" on every row is noise that would bury the rows where it matters. */
function lateBy(a) {
  if (!a.received_date || !a.action_date) return null;
  const gapMs = new Date(a.received_date) - new Date(a.action_date);
  if (gapMs < 5 * 60 * 1000) return null;
  const hours = gapMs / (1000 * 60 * 60);
  if (hours < 1) return `${Math.round(gapMs / 60000)} minutes later`;
  if (hours < 48) return `${Math.round(hours)} hours later`;
  return `${Math.round(hours / 24)} days later`;
}

export function OverviewTab({ hive, actions, photos }) {
  // Shown by default: somebody who just put frames on a hive should see
  // it happen. The switch is for later, when the season's kit movements
  // are burying the inspections.
  const [showEquipment, setShowEquipment] = useState(true);
  const equipmentCount = (actions || []).filter((a) => EQUIPMENT_ACTIONS.has(a.action_type)).length;
  const shown = showEquipment
    ? actions
    : (actions || []).filter((a) => !EQUIPMENT_ACTIONS.has(a.action_type));

  return (
    <div>
      {/* Conditions at this hive's site (#273). Above the timeline
          because it answers a question about right now, and the timeline
          answers one about the past. */}
      {hive?.site_id && (
        <Card title="Conditions" className="section-spacing">
          <WeatherPanel
            siteId={hive.site_id}
            siteName={hive.site_name}
            latitude={hive.site_latitude}
            longitude={hive.site_longitude}
          />
        </Card>
      )}

      <Card
        title="Timeline"
        className="section-spacing"
        actions={
          // Offered only when there is something to hide. A switch that
          // does nothing is a question the reader has to answer for no
          // reason.
          equipmentCount > 0 ? (
            <label style={{ display: "flex", alignItems: "center", gap: "var(--space-1)", fontSize: "var(--font-size-sm)" }}>
              <input id="overview-tab-show-equipment" name="show-equipment"
                type="checkbox"
                checked={showEquipment}
                onChange={(e) => setShowEquipment(e.target.checked)}
              />
              Show equipment ({equipmentCount})
            </label>
          ) : null
        }
      >
        {shown.length === 0 ? (
          <EmptyState title="No actions logged yet" />
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {shown.map((a) => (
              <li key={a.action_id} style={{ display: "flex", gap: "var(--space-3)", padding: "var(--space-2) 0", borderBottom: "1px solid var(--color-border)" }}>
                <span aria-hidden="true">{ACTION_ICONS[a.action_type] || "•"}</span>
                <div>
                  <strong>{ACTION_LABELS[a.action_type] || a.action_type}</strong>
                  <div style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
                    {new Date(a.action_date).toLocaleString()}
                    {a.performed_by ? ` — ${a.performed_by}` : ""}
                    {lateBy(a) ? ` · logged offline, synced ${lateBy(a)}` : ""}
                  </div>
                  {describeActionDetails(a.details) && (
                    <p style={{ margin: "var(--space-1) 0 0", whiteSpace: "pre-wrap" }}>
                      {describeActionDetails(a.details)}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="Photo Gallery">
        {!photos || photos.length === 0 ? (
          <EmptyState icon="📷" title="No photos yet" description="Photos attached to inspections will appear here." />
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: "var(--space-2)" }}>
            {photos.map((p) => (
              <img key={p.photo_id} src={p.photo_url} alt={p.caption || "Hive photo"} style={{ width: "100%", borderRadius: "var(--radius-md)", objectFit: "cover", aspectRatio: "1" }} />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
